import { FC, useEffect } from "react";

import { useCanDeleteActions } from "../../../utils/delete";

import { TopicNavigationButtons } from "./TopicNavigationButtons";

import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { TopicSidebarOptionsMenu } from "components/menus/TopicSidebarOptionsMenu";
import type { TopicType } from "components/server-types";
import { t } from "i18n/i18n";
import { DropdownItemType, DropdownMenu } from "mds/components/DropdownMenu";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { PageListIcon } from "mds/icons/PageList";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentCourseId, selectVisibleTopicsWithPages } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { changeReleaseStatusForPages } from "utils/page";
import { useNavigate } from "utils/routing";
import { useTopicActions } from "utils/topic";
import { pageUrl } from "utils/urls";

export type TopicSidebarPanelHeaderProps = {
  topic: TopicType;
  onDelete: () => void;
};

// This panel displays each type of activity and its child pages for a given topic.
// It is also for selecting the individual page/worksheet to be displayed in main page.
export const TopicSidebarPanelHeader: FC<TopicSidebarPanelHeaderProps> = ({ topic, onDelete }) => {
  const { canAuthorCourse } = useCapabilities();
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const visibleTopics = useListSelector(selectVisibleTopicsWithPages);
  const currentTopic = visibleTopics.find((visibleTopic) => visibleTopic.id === topic.id)!;
  const navigate = useNavigate();

  const topicItems = visibleTopics.map((topicItem) => ({
    id: topicItem.id,
    title: topicItem.title,
  }));

  const isSmallScreen = !useIsMdOrLarger();
  const { canDeleteTopic } = useCanDeleteActions(topic.id);

  const { saveTopic } = useTopicActions();

  useEffect(() => {
    if (currentCourseId) {
      storeApi.topics.list({ section_id: currentCourseId });
      // TODO: The topic sidebar dropdown won't work correctly for students when they load a page
      // directly, because we haven't yet loaded any activities/pages for the topics, which the
      // store selector relies on to calculate visible topics. Loading all pages/activities is not
      // an adequate solution for that, and we should find a better way to handle this.
      storeApi.pages.list({ section_id: currentCourseId });
      storeApi.activities.list({ section_id: currentCourseId });
    }
  }, [currentCourseId]);

  const onTopicTitleChanged = (newTitle: string) => {
    saveTopic({ id: topic.id, title: newTitle });
  };

  const onTopicChange = (item: DropdownItemType) => {
    if (item.id) {
      onNavigateToTopic(item.id);
    }
  };

  const onNavigateToTopic = (topicId: string) => {
    navigate(pageUrl(currentCourseId, "", { topicId }));
  };

  return (
    <div className="mt-[1px]">
      <div className="flex items-center justify-between">
        <DropdownMenu
          buttonText={
            <span className="!font-medium text-black-tint-20">{t("glossary.topic")}</span>
          }
          items={topicItems}
          kind="tertiary"
          menuProps={{ position: "initial", align: "start", direction: "bottom" }}
          openMenuEventName="Sidebar - Content / Topic dropdown"
          selectedValue={topic?.id}
          size="xs"
          truncateWidth="max-w-[334px]"
          onSelectionChange={(item) => onTopicChange(item)}
        />

        <TopicNavigationButtons
          currentTopicId={topic.id}
          topics={visibleTopics}
          onChange={onNavigateToTopic}
        />
      </div>

      <HoverableToolbar
        contentClassName="mt-0.5 bg-black-tint-95"
        readOnly={!canAuthorCourse}
        reference="topic"
        disableUnlessEditMode
        showEditButton
        uncentered
      >
        <h3 className="h3 my-0 ml-2 flex w-full items-center gap-2 text-black-tint-20">
          {isSmallScreen && <PageListIcon className="text-blue" />}
          <HoverableEditField
            label={t("glossary.topic_title")}
            textSize="h3"
            value={topic.title}
            disableUnlessEditMode
            shouldWrap
            onValueChanged={onTopicTitleChanged}
          />
        </h3>

        <TopicSidebarOptionsMenu
          optionsMenuActions={{
            onDelete,
            isDeleteDisabled: !canDeleteTopic,
            deleteTooltipText: canDeleteTopic
              ? undefined
              : t("tooltip.delete_topic_has_assessments"),
            onChangeStatus: () => changeReleaseStatusForPages(currentTopic?.pages || []),
          }}
          releasedCount={topic.released_page_count}
          unreleasedCount={topic.unreleased_page_count}
        />
      </HoverableToolbar>
    </div>
  );
};
